import { type AbstractMesh, type AnimationGroup, ImportMeshAsync, type Scene, type Skeleton } from "@babylonjs/core";
import { fileURL } from "../net/api";

/** A GLB loaded into the room scene (spec 2o §4.3). */
export interface LoadedModel {
  meshes: AbstractMesh[];
  groups: AnimationGroup[];
  skeletons: Skeleton[];
  dispose(): void;
}

export type ModelLoader = (scene: Scene, code: string) => Promise<LoadedModel>;

/** Loads an asset's GLB with every clip stopped; the caller decides what plays. */
export const loadGLB: ModelLoader = async (scene, code) => {
  const r = await ImportMeshAsync(fileURL(code), scene, { pluginExtension: ".glb" });
  for (const g of r.animationGroups) g.stop();
  return {
    meshes: r.meshes, groups: r.animationGroups, skeletons: r.skeletons,
    dispose() {
      for (const g of r.animationGroups) g.dispose();
      for (const s of r.skeletons) s.dispose();
      for (const m of r.meshes) m.dispose(false, true);
    },
  };
};

/** The meshes the loader did not parent, to hang under a holder node. */
export function roots(model: LoadedModel): AbstractMesh[] {
  return model.meshes.filter((m) => !m.parent);
}

/** Clip names and lengths in seconds as Babylon loaded them (spec 2o §2.4). */
export function clipInfo(groups: readonly AnimationGroup[]): { name: string; duration: number }[] {
  return groups.map((g, i) => {
    const fps = g.targetedAnimations[0]?.animation.framePerSecond ?? 60;
    return { name: g.name || `clip ${i + 1}`, duration: Math.max(0, (g.to - g.from) / fps) };
  });
}
